import { AlertCircle, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Background } from './components/Background'
import { Landing } from './components/Landing'
import { Lead } from './components/Lead'
import { Quiz } from './components/Quiz'
import { Report } from './components/Report'
import { Button } from './components/ui'
import { loadConfig, submitDiagnostico } from './lib/api'
import { classify } from './lib/classify'

export default function App() {
  // loading | landing | triagem | aprofundamento | lead | submitting | report | error
  const [phase, setPhase] = useState('loading')
  const [data, setData] = useState(null) // { tipo, config, triagem, blocos }
  const [respostas, setRespostas] = useState({})
  const [deep, setDeep] = useState([])
  const [resultado, setResultado] = useState(null)
  const [erro, setErro] = useState('')
  const startedAt = useRef(Date.now())
  const respostasRef = useRef({})

  useEffect(() => {
    loadConfig()
      .then((d) => {
        setData(d)
        setPhase('landing')
      })
      .catch((e) => {
        setErro(e.message || 'Nao consegui carregar o diagnostico.')
        setPhase('error')
      })
  }, [])

  // Mapa opcao -> voto, pra classificar no cliente.
  const votaByOpcao = useMemo(() => {
    const m = {}
    for (const p of data?.triagem ?? []) for (const o of p.opcoes) if (o.vota) m[o.codigo] = o.vota
    return m
  }, [data])

  const triagemPerguntas = useMemo(
    () => (data?.triagem ?? []).map((p) => ({ ...p, label: 'Pra começar' })),
    [data],
  )

  function answer(perguntaCodigo, opcaoCodigo) {
    const next = { ...respostasRef.current, [perguntaCodigo]: opcaoCodigo }
    respostasRef.current = next
    setRespostas(next)
  }

  function onTriagemComplete() {
    const cl = classify(data.config, respostasRef.current, votaByOpcao)
    if (cl.primary === 'saudavel') {
      setDeep([])
      setPhase('lead')
      scrollTop()
      return
    }
    const arq = data.config.arquetipos?.[cl.primary]
    const bloco = (data.blocos[cl.primary] ?? []).map((p) => ({
      ...p,
      label: arq?.rotulo ?? 'Aprofundando',
    }))
    // 1 pergunta de confirmacao do gargalo secundario.
    const conf =
      cl.secondary && data.blocos[cl.secondary]?.[0]
        ? [{ ...data.blocos[cl.secondary][0], label: 'Confirmando' }]
        : []
    const lista = [...bloco, ...conf]
    setDeep(lista)
    setPhase(lista.length ? 'aprofundamento' : 'lead')
    scrollTop()
  }

  async function submit(leadData, turnstileToken) {
    setPhase('submitting')
    setErro('')
    try {
      const r = await submitDiagnostico({
        ...leadData,
        'cf-turnstile-response': turnstileToken,
        respostas: respostasRef.current,
        tempo_preenchimento_segundos: Math.round((Date.now() - startedAt.current) / 1000),
      })
      setResultado(r)
      setPhase('report')
      scrollTop()
    } catch (e) {
      setErro(e.message || 'Falha ao enviar. Tente de novo.')
      setPhase('lead')
    }
  }

  return (
    <Background>
      <main className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col items-center justify-center px-5 py-10">
        {phase === 'loading' && (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <p className="text-sm">Preparando seu raio-x...</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="glass max-w-md rounded-3xl p-10 text-center">
            <AlertCircle className="mx-auto mb-3 h-8 w-8 text-danger" />
            <h1 className="mb-2 font-display text-xl font-bold">Algo travou aqui</h1>
            <p className="mb-6 text-sm text-muted-foreground">{erro}</p>
            <Button onClick={() => window.location.reload()}>Tentar de novo</Button>
          </div>
        )}

        {phase === 'landing' && <Landing onStart={() => setPhase('triagem')} />}

        {phase === 'triagem' && (
          <Quiz
            perguntas={triagemPerguntas}
            respostas={respostas}
            onAnswer={answer}
            onComplete={onTriagemComplete}
          />
        )}

        {phase === 'aprofundamento' && (
          <Quiz
            perguntas={deep}
            respostas={respostas}
            onAnswer={answer}
            onComplete={() => {
              setPhase('lead')
              scrollTop()
            }}
          />
        )}

        {(phase === 'lead' || phase === 'submitting') && (
          <Lead submitting={phase === 'submitting'} erro={erro} onSubmit={submit} />
        )}

        {phase === 'report' && <Report resultado={resultado} />}
      </main>
    </Background>
  )
}

function scrollTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
